import { ContentState } from "draft-js";
import { I18n } from "@assets/strings";
import React, { useState } from "react";
import styled from "styled-components";
import { CurrentProfile } from "@domain/currentProfile/currentProfile";
import { Group } from "@domain/group/group";
import { AboutShareroomDraftField, ShareroomPresentation } from "@domain/group/shareroom/shareroom";
import { Media } from "@domain/media/media";
import { useIsMobile } from "@utils/hooks";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { RoundedPlusButton } from "@ui/components/buttons/roundedPlusButton";
import { SecondaryButton } from "@ui/components/buttons/secondaryButton";
import { Description } from "@ui/components/containers/description";
import { DraftRenderer } from "@ui/components/containers/draftRenderer";
import { PrimaryBold } from "@ui/components/containers/primary";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { AdvencedRichInput } from "@ui/components/inputs/draft/advencedRichInput";
import { row, Row, Stack, stack } from "@ui/components/layout";
import { TooshareVideoPlayer } from "@ui/components/media/tooshareVideoPlayer";
import { VideoPlayer } from "@ui/components/media/videoPlayer";
import { desktop, mobile } from "@ui/styles/utils";
import { ShareroomBlocIcons } from "./shareroomInfo";
import { VideoPresentationEditor } from "./videoPresentationEditor";

interface PresentationEditorProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
	shareroom: Group;
	field: AboutShareroomDraftField;
	profile?: CurrentProfile | null;
	onSave: () => void;
	onChange: (value?: ContentState, presentationAsset?: ShareroomPresentation) => void;
}

export const ShareroomAboutEditor: React.FC<PresentationEditorProps> = ({
	shareroom,
	field,
	profile,
	onSave,
	onChange,
	...htmlProps
}) => {
	const { owner } = shareroom;

	const [editMode, setEditMode] = useState(false);

	const isOwner = owner.id === profile?.id;

	const isMobile = useIsMobile();

	return (
		<Container {...htmlProps}>
			<Row align="center" justify="space-between" style={{ width: "100%" }}>
				<StyledPrimarybold>{I18n.t(`shareroom.about.${field}.title`)}</StyledPrimarybold>
				{isOwner && !editMode ? (
					<RoundedPlusButton
						onClick={() => setEditMode(!editMode)}
						label={!shareroom[field]?.hasText() ? I18n.t("add") : I18n.t("edit")}
					/>
				) : null}
			</Row>
			{isOwner && editMode ? (
				<Stack align="center" gap={30} style={{ width: "100%" }}>
					{field === "presentation" ? (
						<VideoPresentationEditor
							presentationAsset={shareroom.presentationAsset as { url?: string; video: Media }}
							onChange={(value) => onChange(undefined, value)}
						/>
					) : null}
					<RichText
						key={field}
						value={shareroom[field] ?? ContentState.createFromText("")}
						onChange={(value) => onChange(value)}
					/>
					{/* {loading ? (
						<ClipLoader />
					) : ( */}
					<Row gap={30}>
						<SecondaryButton onClick={() => setEditMode(false)}>{I18n.t("cancel")}</SecondaryButton>
						<LoadingButton
							onClick={() => {
								setEditMode(false);
								onSave();
							}}
						>
							{I18n.t("save")}
						</LoadingButton>
					</Row>
					{/* )} */}
				</Stack>
			) : !shareroom[field]?.hasText() ? (
				<Stack gap={30} align="center" justify="center" style={{ width: "100%" }}>
					{shareroom.presentationAsset?.url && field === "presentation" ? (
						<StyledVideoPlayer space={isMobile ? 146.25 : 30.25} source={shareroom.presentationAsset?.url} />
					) : shareroom.presentationAsset?.video && field === "presentation" ? (
						<StyledTooshareVideo asset={shareroom.presentationAsset?.video} />
					) : null}
					<IconView>
						<img src={ShareroomBlocIcons[field]} />
					</IconView>

					<Description>{I18n.t(`shareroom.about.${field}.${isOwner ? "owner" : "member"}.description`)}</Description>
				</Stack>
			) : shareroom[field] !== undefined ? (
				<Stack gap={0} style={{ width: "100%" }}>
					{shareroom.presentationAsset?.url && field === "presentation" ? (
						<StyledVideoPlayer space={isMobile ? 146.25 : 30.25} source={shareroom.presentationAsset?.url} />
					) : shareroom.presentationAsset?.video && field === "presentation" ? (
						<StyledTooshareVideo asset={shareroom.presentationAsset?.video} />
					) : null}
					<DraftRenderer content={shareroom[field] as ContentState} />
				</Stack>
			) : null}
		</Container>
	);
};

const Container = styled(RoundedBlock)`
	padding: 30px 40px;
	${stack(30)}
	width:100%;
	@media ${mobile} {
		padding: 20px;
	}
`;

const RichText = styled(AdvencedRichInput)<{ isFocused?: boolean; isInvalid?: boolean }>`
	${({ theme }) => theme.containerStyles.roundedBlockStyle};
	border-color: ${({ theme, isFocused, isInvalid }) =>
		isInvalid ? theme.colors.red : isFocused ? theme.colors.secondary : theme.colors.lightergray};
	cursor: text;

	.public-DraftEditor-content,
	.public-DraftEditorPlaceholder-root {
		padding: 15px;
		font-size: 0.9375rem;
	}
	width: 100%;
	.DraftEditor-editorContainer {
		min-height: 200px;
	}
`;

const StyledPrimarybold = styled(PrimaryBold)`
	color: ${({ theme }) => theme.colors.secondary};
	font-size: 1.5rem;
	font-weight: 800;

	@media ${mobile} {
		font-size: 0.813rem;
	}
`;

const IconView = styled.div`
	${row(0, "center", "center")}
	width: 100%;
	height: 100%;
	> img {
		@media ${desktop} {
			width: 347px;
			height: 217px;
		}
	}
`;

const StyledTooshareVideo = styled(TooshareVideoPlayer)`
	width: 100%;
	height: 328px;
	margin-bottom: 60px !important;
	.quality-control {
		display: none;
	}
`;

const StyledVideoPlayer = styled(VideoPlayer)`
	> iframe {
		height: 328px;
	}
	> div {
		padding-top: 0 !important;
	}
`;
